import { tw } from "@careinfo/tailwind";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { toast } from "react-toastify";
import APIS from "../../constant/Endpoint";
import useDeleteHook from "../../hooks/useDeleteHook";
import Button from "../../shared/Button";
import DataTable from "../../shared/DataTable";
import ActionCell from "../../shared/DataTable/ActionCell";
import NACell from "../../shared/DataTable/NACell";
import Modal from "../../shared/Modal";
import { IComponent } from "../../types/interface";
import { useNavigate } from "react-router-dom";

interface Props {
  showDeleteModal: boolean;
  setShowDeleteModal: Dispatch<SetStateAction<boolean>>;
  handleDelete: (id: string) => Promise<void>;
  isDeletingComponent: boolean;
}

export default function ComponentsList({
  showDeleteModal,
  setShowDeleteModal,
  handleDelete,
  isDeletingComponent,
}: Props) {
  const navigate = useNavigate();
  const [toDelete, setToDelete] = useState<string | null>(null);

  const { mutateAsync: deleteAll } = useDeleteHook({
    queryKey: ["delete-component"],
  });

  const handleDeleteClick = (id: string) => {
    setToDelete(id);
    setShowDeleteModal(true);
  };

  const handleModalClose = () => {
    setToDelete(null);
    setShowDeleteModal(false);
  };

  const columns: ColumnDef<IComponent>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: NACell,
    },
    {
      header: "Action",
      accessorKey: "id",
      cell: (props: CellContext<any, string>) => (
        <ActionCell
          {...props}
          // editPath={`edit/${props.getValue()}`}
          handleDeleteClick={handleDeleteClick}
        />
      ),
    },
  ];

  useEffect(() => {
    if (!showDeleteModal) {
      setToDelete(null);
    }
  }, [showDeleteModal]);
  const handleNavigate = (row) => {
    navigate(`edit/${row?.original?.id}`);
  };

  return (
    <div>
      <DataTable
        url={`${APIS.COMPONENT}`}
        queryKey={"component"}
        columns={columns}
        addNewPagePath="create"
        tableName="Components"
        handleRowClick={handleNavigate}
      >
        <DataTable.ActionMenu>
          <div className={tw`w-24`}>
            <Button
              label="Delete all"
              onClick={async () => {
                try {
                  const url = APIS.COMPONENT + "deleteAll/";
                  await deleteAll({ url });
                } catch (e) {
                  toast.error("Unable to delete", e.message);
                }
              }}
            />
          </div>
        </DataTable.ActionMenu>
      </DataTable>
      <Modal
        isOpen={showDeleteModal}
        setIsOpen={setShowDeleteModal}
        closeModal={handleModalClose}
        title="Are you sure, want to delete this component?"
        width="20rem"
      >
        <Modal.Caution>
          <p className={tw`py-2`}>
            <span className={tw`font-bold text-primary text-center`}>
              Note:{"    "}
            </span>
            This action can not be undone
          </p>
        </Modal.Caution>
        <div className={tw`grid grid-cols-2 gap-x-4 items-center`}>
          <Button
            label="Yes, Delete"
            type="button"
            onClick={() => handleDelete(toDelete)}
            isLoading={isDeletingComponent}
            disabled={isDeletingComponent || !toDelete}
          />
          <button
            type="button"
            onClick={() => {
              setToDelete(null);
              setShowDeleteModal(false);
            }}
            className={tw`w-full bg-gray-400 text-white px-4 py-2 rounded-md focus:outline-none`}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
}
